/* eslint-disable no-nested-ternary */
import classNames from 'classnames';
import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Mail,
  Phone,
  ChevronDown,
  ChevronUp,
  InfoCircle,
  LayoutColumns,
  Diamond,
  Building,
} from '@saleshandy/icons';
import { RootState } from '../../../../store/root-reducer';
import {
  setSelectedComponent,
  setExportColumns,
} from '../../csv-enrichment-slice';
import {
  EnrichmentComponent,
  EnrichmentType,
} from '../../enums/csv-enrichment.enums';
import { LeadFinderCredits } from '../../types/csv-enrichment.types';
import Checkbox from '../../../../shared/design-system/components/atoms/checkbox';
import {
  PEOPLE_EXPORT_GROUPS,
  COMPANY_EXPORT_GROUPS,
  COMPANY_REQUIRED_KEYS,
} from '../../helpers/export-column-groups';
import type { ExportColumnGroup } from '../../helpers/export-column-groups';
import {
  OverlayTooltip,
  Placement,
} from '../../../../shared/design-system/components/overlay';
import { getIsDemoAccount } from '../../../../shared/utils/user-details';
import { PageHeaderAlertTypes } from '../../../../shared/utils/page-header-alert-types';

const EMAIL_TAGS = ['Email', 'Job Title', 'Company', 'Location'];
const PHONE_EMAIL_TAGS = ['Phone', 'Email', 'Job Title', 'Company', 'Location'];
const COMPANY_TAGS = [
  'Company Name',
  'Company Website',
  'Company Industry',
  'Last Funding Round',
  'Competitor Company',
];

const ChooseComponentsStep: React.FC = () => {
  const dispatch = useDispatch();
  const [isExportExpanded, setIsExportExpanded] = useState(true);

  const { selectedComponent, exportColumns, selectedType } = useSelector(
    (state: RootState) => state.csvEnrichment,
  );
  const leadFinderCredits = useSelector(
    (state: RootState) => (state.home as any)?.leadFinder?.credits,
  ) as LeadFinderCredits | undefined;

  const pageHeaderBannerAlert = useSelector(
    (state: RootState) => state?.pageHeader?.bannerAlert,
  );

  const emailCredits = leadFinderCredits?.email;
  const emailPhoneCredits = leadFinderCredits?.emailphone;
  const companyCredits = leadFinderCredits?.company;

  const hasPaymentPendingBanner =
    pageHeaderBannerAlert === PageHeaderAlertTypes.PAYMENT_PENDING;

  useEffect(() => {
    if (selectedComponent) return;
    if (!selectedType) return;

    dispatch(setSelectedComponent(EnrichmentComponent.EMAIL));
  }, [dispatch, selectedComponent, selectedType]);

  const groups =
    selectedType === EnrichmentType.COMPANY
      ? COMPANY_EXPORT_GROUPS
      : PEOPLE_EXPORT_GROUPS;

  const displayGroups = useMemo(() => {
    if (selectedType !== EnrichmentType.COMPANY) {
      const shouldDisableEmail = Boolean(selectedComponent);
      const shouldDisableName = Boolean(selectedComponent);
      const shouldDisableCompanyName = Boolean(selectedComponent);
      const shouldDisableCompanyWebsite = Boolean(selectedComponent);
      const shouldDisablePhone =
        selectedComponent === EnrichmentComponent.PHONE_EMAIL;
      const showPhoneExportColumn =
        selectedComponent === EnrichmentComponent.PHONE_EMAIL;

      return groups?.map((g) => ({
        ...g,
        columns: g.columns
          ?.filter((c) => showPhoneExportColumn || c.key !== 'phones')
          ?.map((c) =>
            c.key === 'emails'
              ? { ...c, disabled: shouldDisableEmail }
              : c.key === 'phones'
              ? { ...c, disabled: shouldDisablePhone }
              : c.key === 'full_name'
              ? { ...c, disabled: shouldDisableName }
              : c.key === 'company_name'
              ? { ...c, disabled: shouldDisableCompanyName }
              : c.key === 'company_website'
              ? { ...c, disabled: shouldDisableCompanyWebsite }
              : c,
          ),
      }));
    }

    if (selectedType === EnrichmentType.COMPANY) {
      return groups?.map((g) => ({
        ...g,
        columns: g.columns?.map((c) =>
          COMPANY_REQUIRED_KEYS.includes(c.key) ? { ...c, disabled: true } : c,
        ),
      }));
    }

    return groups;
  }, [groups, selectedComponent, selectedType]);

  useEffect(() => {
    const allGroupKeys = displayGroups
      .flatMap((g) => g.columns)
      .map((c) => c.key);
    const hasNewKeys = exportColumns.some((k) => allGroupKeys.includes(k));
    if (!hasNewKeys) {
      const allSelectableKeys = displayGroups
        .flatMap((g) => g.columns)
        .filter((c) => !c.disabled)
        .map((c) => c.key);
      dispatch(setExportColumns(allSelectableKeys));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const disabledKeys = displayGroups
      .flatMap((g) => g.columns)
      .filter((c) => c.disabled)
      .map((c) => c.key);

    const requiredExportKeys: string[] = [];
    if (selectedType !== EnrichmentType.COMPANY && selectedComponent) {
      if (selectedComponent === EnrichmentComponent.EMAIL) {
        requiredExportKeys.push(
          'emails',
          'full_name',
          'company_name',
          'company_website',
        );
      }
      if (selectedComponent === EnrichmentComponent.PHONE_EMAIL) {
        requiredExportKeys.push(
          'emails',
          'phones',
          'full_name',
          'company_name',
          'company_website',
        );
      }
    }

    if (selectedType === EnrichmentType.COMPANY && selectedComponent) {
      requiredExportKeys.push(...COMPANY_REQUIRED_KEYS);
    }

    const merged = Array.from(
      new Set([
        ...(exportColumns ?? []),
        ...disabledKeys,
        ...requiredExportKeys,
      ]),
    );

    if (merged.length !== exportColumns.length) {
      dispatch(setExportColumns(merged));
    }
  }, [dispatch, displayGroups, exportColumns, selectedComponent, selectedType]);

  // When switching reveal type, reset forced selections accordingly.
  useEffect(() => {
    if (selectedType === EnrichmentType.COMPANY) return;
    if (!selectedComponent) {
      // No reveal type selected → don't preselect contact fields
      const current = exportColumns ?? [];
      const next = current.filter(
        (k) =>
          k !== 'emails' &&
          k !== 'phones' &&
          k !== 'full_name' &&
          k !== 'company_name' &&
          k !== 'company_website',
      );
      if (next.length !== current.length) {
        dispatch(setExportColumns(next));
      }
      return;
    }

    const current = exportColumns ?? [];
    let next = current;

    if (selectedComponent === EnrichmentComponent.EMAIL) {
      next = Array.from(
        new Set([
          ...current,
          'emails',
          'full_name',
          'company_name',
          'company_website',
        ]),
      ).filter((k) => k !== 'phones');
    }

    if (selectedComponent === EnrichmentComponent.PHONE_EMAIL) {
      next = Array.from(
        new Set([
          ...current,
          'emails',
          'phones',
          'full_name',
          'company_name',
          'company_website',
        ]),
      );
    }

    if (next.length !== current.length) {
      dispatch(setExportColumns(next));
    }
  }, [dispatch, exportColumns, selectedComponent, selectedType]);

  const allSelectableKeys = useMemo(
    () =>
      displayGroups
        .flatMap((g) => g.columns)
        .filter((c) => !c.disabled)
        .map((c) => c.key),
    [displayGroups],
  );

  /** Every visible export column (includes locked Email / Phone); used for badge and select-all state */
  const allDisplayedColumnKeys = useMemo(
    () =>
      Array.from(
        new Set(displayGroups.flatMap((g) => g.columns).map((c) => c.key)),
      ),
    [displayGroups],
  );

  const lockedColumnKeys = useMemo(
    () =>
      displayGroups
        .flatMap((g) => g.columns)
        .filter((c) => c.disabled)
        .map((c) => c.key),
    [displayGroups],
  );

  const totalSelected = allDisplayedColumnKeys.filter((k) =>
    exportColumns.includes(k),
  ).length;
  const isSelectAll =
    allDisplayedColumnKeys.length > 0 &&
    allDisplayedColumnKeys.every((k) => exportColumns.includes(k));
  const isIndeterminate =
    totalSelected > 0 && totalSelected < allDisplayedColumnKeys.length;

  const getGroupState = (group: ExportColumnGroup) => {
    const selectableKeys = group.columns
      .filter((c) => !c.disabled)
      .map((c) => c.key);
    const columnKeys = group.columns.map((c) => c.key);
    const checkedCount = columnKeys.filter((k) => exportColumns.includes(k))
      .length;
    const total = group.columns.length;
    return {
      checked: checkedCount === total && total > 0,
      indeterminate: checkedCount > 0 && checkedCount < total,
      checkedCount,
      total,
      selectableKeys,
    };
  };

  const handleComponentSelect = (component: EnrichmentComponent) => {
    dispatch(setSelectedComponent(component));
  };

  const handleSelectAllToggle = () => {
    if (isSelectAll) {
      dispatch(setExportColumns(lockedColumnKeys));
      return;
    }
    dispatch(setExportColumns([...allDisplayedColumnKeys]));
  };

  const handleGroupToggle = (group: ExportColumnGroup) => {
    const { checked, selectableKeys } = getGroupState(group);
    const updated = checked
      ? exportColumns.filter((k) => !selectableKeys.includes(k))
      : Array.from(new Set([...exportColumns, ...selectableKeys]));
    dispatch(setExportColumns(updated));
  };

  const handleColumnToggle = (colKey: string) => {
    const updated = exportColumns.includes(colKey)
      ? exportColumns.filter((k) => k !== colKey)
      : [...exportColumns, colKey];
    dispatch(setExportColumns(updated));
  };

  const COMPONENT_CARDS = useMemo(
    () =>
      selectedType === EnrichmentType.COMPANY
        ? [
            {
              value: EnrichmentComponent.EMAIL,
              icon: Building,
              title: 'Enrich Full Profile',
              credits: companyCredits,
              description:
                'Find companies with full enrichment data including revenue, employee data, technologies, funding, and industry details.',
              tags: COMPANY_TAGS,
            },
          ]
        : [
            {
              value: EnrichmentComponent.EMAIL,
              icon: Mail,
              title: 'Email Addresses',
              credits: emailCredits,
              description:
                'Find verified business email addresses along with full profile data.',
              tags: EMAIL_TAGS,
            },
            {
              value: EnrichmentComponent.PHONE_EMAIL,
              icon: Phone,
              title: 'Phone + Email Reveal',
              credits: emailPhoneCredits,
              description:
                'Get mobile numbers and verified email along with full profile data.',
              tags: PHONE_EMAIL_TAGS,
            },
          ],
    [selectedType, companyCredits, emailCredits, emailPhoneCredits],
  );

  return (
    <div className="bs-csv-enrichment__choose-components">
      <p className="regular-2 gray-txt-15 text-center mb-4">
        {selectedType === EnrichmentType.COMPANY
          ? 'Configure columns for your company enrichment'
          : 'Choose which contact info you want to enrich'}
      </p>

      <div className="bs-csv-enrichment__component-cards">
        {COMPONENT_CARDS.map((card) => {
          const IconComponent = card.icon;
          const isSelected = selectedComponent === card.value;
          const isPhoneEmail = card.value === EnrichmentComponent.PHONE_EMAIL;
          const iconModifierClass = isPhoneEmail
            ? 'bs-csv-enrichment__component-card-icon--phone-email'
            : 'bs-csv-enrichment__component-card-icon--email';
          return (
            <button
              key={card.value}
              type="button"
              className={`bs-csv-enrichment__component-card ${
                isSelected ? 'bs-csv-enrichment__component-card--selected' : ''
              }`}
              onClick={() => handleComponentSelect(card.value)}
            >
              <div className="bs-csv-enrichment__component-card-header">
                <div className="d-flex align-items-center gap-8">
                  <div
                    className={`bs-csv-enrichment__radio ${
                      isSelected ? 'bs-csv-enrichment__radio--selected' : ''
                    }`}
                  />
                  <div
                    className={`bs-csv-enrichment__component-card-icon ${iconModifierClass}`}
                  >
                    <IconComponent width={20} height={20} />
                  </div>
                  <span className="title">{card.title}</span>
                </div>
                <span className="semibold-2 blue-txt-11 d-flex align-items-center gap-1">
                  {card.credits}
                  <Diamond width={16} height={16} />
                </span>
              </div>
              <p className="description">{card.description}</p>
              <div className="bs-csv-enrichment__component-tags">
                {card.tags.map((tag) => (
                  <span key={tag} className="bs-csv-enrichment__tag">
                    {tag}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      <div className="bs-csv-enrichment__export-section-grouped">
        {/* Header bar */}
        <div
          className={classNames('bs-csv-enrichment__export-header', {
            'bs-csv-enrichment__export-header--expanded': isExportExpanded,
          })}
          onClick={() => setIsExportExpanded(!isExportExpanded)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setIsExportExpanded(!isExportExpanded);
            }
          }}
        >
          <div className="bs-csv-enrichment__export-header-left">
            <LayoutColumns
              width={16}
              height={16}
              color="#9ca3af"
              className="d-flex align-items-center"
            />
            <span className="semibold-2 gray-txt-9 d-flex align-items-center">
              Select columns to export
            </span>
            <OverlayTooltip
              text="Adding or removing columns does not affect your credit consumption. Credits are only based on your selected enrichment components above."
              placement={Placement.Bottom}
            >
              <InfoCircle
                width={14}
                height={14}
                color="#6b7280"
                className="d-flex align-items-center"
              />
            </OverlayTooltip>
            <span className="bs-csv-enrichment__export-count-badge d-flex align-items-center">
              {totalSelected}/{allDisplayedColumnKeys.length}
            </span>
          </div>
          <div className="bs-csv-enrichment__export-header-right">
            <div onClick={(e) => e.stopPropagation()} role="presentation">
              <Checkbox
                checked={isSelectAll}
                intermediate={isIndeterminate}
                onChange={handleSelectAllToggle}
                label="Select All"
              />
            </div>
            {isExportExpanded ? (
              <ChevronUp width={14} height={14} color="#9CA3AF" />
            ) : (
              <ChevronDown width={14} height={14} color="#9CA3AF" />
            )}
          </div>
        </div>

        {/* Groups */}
        {isExportExpanded && (
          <div
            className={classNames('bs-csv-enrichment__export-groups', {
              'bs-csv-enrichment__export-groups--people':
                selectedType !== EnrichmentType.COMPANY,
              'bs-csv-enrichment__export-groups--demo-account':
                getIsDemoAccount() || hasPaymentPendingBanner,
            })}
          >
            {displayGroups.map((group) => {
              const {
                checked,
                indeterminate,
                checkedCount,
                total,
              } = getGroupState(group);
              return (
                <div
                  key={group.title}
                  className="bs-csv-enrichment__export-group"
                >
                  {/* Group header */}
                  <div className="bs-csv-enrichment__export-group-header">
                    <div className="bs-csv-enrichment__export-group-header-left">
                      <Checkbox
                        checked={checked}
                        intermediate={indeterminate}
                        onChange={() => handleGroupToggle(group)}
                        label={
                          <span className="semibold-2 blue-txt-15">
                            {group.title}
                          </span>
                        }
                      />
                    </div>
                    <span className="bs-csv-enrichment__export-group-count">
                      {checkedCount}/{total}
                    </span>
                  </div>

                  {/* 3-column grid */}
                  <div className="bs-csv-enrichment__export-columns-grid">
                    {group.columns.map((col) => (
                      <div
                        key={col.key}
                        className="bs-csv-enrichment__export-col-item"
                      >
                        <Checkbox
                          checked={
                            col.disabled
                              ? true
                              : exportColumns.includes(col.key)
                          }
                          disabled={col.disabled}
                          onChange={() =>
                            !col.disabled && handleColumnToggle(col.key)
                          }
                          label={col.label}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChooseComponentsStep;
